import { WithApiRequestMeta } from "../../api-results";
import { RtkEmptySplitApi } from "../../types";
import { ChatMessageModel } from "./models/ChatMessage";
import {
  KlipyGifListResponseDto,
  KlipyGifModel,
  transformKlipyGifDtoToModel,
} from "./models/KlipyGif";
import { ChatMessagesApiFetchParams, KlipyGifsQueryParams } from "./payloads";
import { ChatsApiTags } from "./tags";

export const addChatsApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [ChatsApiTags.getMessages, ChatsApiTags.pin],
  });

  const chatsApi = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
      messages: builder.query<
        { data: ChatMessageModel[]; remaining_item: boolean },
        WithApiRequestMeta<{
          params: ChatMessagesApiFetchParams;
        }>
      >({
        query: ({ params }) => ({
          url: `/api/auth/messages`,
          params: {
            ...params,
            time: params.time ? params.time.replace("T", " ") : undefined,
          },
          transformResponse: (response: string) => {
            const parsedResponse: {
              data: ChatMessageModel[];
              remaining_item: boolean;
            } = JSON.parse(response);
            return {
              ...parsedResponse,
              data: parsedResponse.data,
            };
          },
        }),
        providesTags: [ChatsApiTags.getMessages],
      }),
      getPinnedMessages: builder.query<
        { data: ChatMessageModel[] },
        WithApiRequestMeta<{
          entityId: string;
          chatId: string;
        }>
      >({
        query: ({ entityId, chatId }) => ({
          url: `/api/auth/messages/${entityId}/pin`,
          params: {
            chat_id: chatId,
          },
        }),
        providesTags: (_result, _error, { chatId }) => [
          { type: ChatsApiTags.pin, id: chatId },
        ],
      }),
      pinMessages: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          chatId: string;
          msgReceiptIds: string[];
          pin: boolean;
        }>
      >({
        query: ({ entityId, chatId, msgReceiptIds, pin }) => ({
          url: `/api/auth/messages/${entityId}/pin`,
          method: "PUT",
          data: {
            pin,
            chat_id: chatId,
            msg_receipt_ids: msgReceiptIds,
          },
        }),
        invalidatesTags: (_result, _error, { chatId, pin }) =>
          pin ? [{ type: ChatsApiTags.pin, id: chatId }] : [],
        onQueryStarted: async (
          { pin, chatId, entityId, msgReceiptIds },
          { dispatch, queryFulfilled }
        ) => {
          if (pin) return;

          try {
            await queryFulfilled;

            dispatch(
              chatsApi.util.updateQueryData(
                "getPinnedMessages",
                { entityId, chatId },
                (pinnedMessagesDraft) => {
                  pinnedMessagesDraft.data = pinnedMessagesDraft.data.filter(
                    (pinnedMessage) =>
                      !msgReceiptIds.includes(pinnedMessage.msg_receipt_id)
                  );
                }
              )
            );
          } catch {
            // Do nothing
          }
        },
      }),
      message: builder.query<
        { data: ChatMessageModel[]; remaining_item: boolean },
        WithApiRequestMeta<{
          msg_id: string;
        }>
      >({
        query: ({ msg_id }) => ({
          url: `/api/auth/messages/msg_id/${msg_id}`,
          transformResponse: (response: string) => {
            const parsedResponse: {
              data: ChatMessageModel[];
              remaining_item: boolean;
            } = JSON.parse(response);
            return {
              ...parsedResponse,
              data: parsedResponse.data,
            };
          },
        }),
      }),
      deleteConversation: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; chatId: string }>
      >({
        query: ({ chatId, entityId }) => ({
          url: "/api/auth/messages/delete_conversation",
          method: "DELETE",
          data: { entity_id: entityId, workspace_id: chatId },
        }),
      }),
      getKlipyGifs: builder.query<
        { data: KlipyGifModel[]; has_next: boolean },
        WithApiRequestMeta<KlipyGifsQueryParams>
      >({
        query: ({ apiKey, customerId, page, pageSize = 24, query }) => {
          const trimmedQuery = query?.trim();
          const path = trimmedQuery ? "gifs/search" : "gifs/trending";

          return {
            url: `https://api.klipy.com/api/v1/${apiKey}/${path}`,
            params: {
              page,
              per_page: pageSize,
              customer_id: customerId,
              format_filter: "gif",
              content_filter: "medium",
              ...(trimmedQuery ? { q: trimmedQuery } : {}),
            },
            isPublic: true,
          };
        },
        transformResponse: (response: KlipyGifListResponseDto) => ({
          has_next: Boolean(response.data?.has_next),
          data: (response.data?.data ?? [])
            .filter((dto) => dto.type !== "ad")
            .map(transformKlipyGifDtoToModel)
            .filter((item): item is KlipyGifModel => item !== null),
        }),
      }),
    }),
  });

  return chatsApi;
};

export type ChatsApi = ReturnType<typeof addChatsApi>;
